import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Lock, Loader2, Building2, ShieldCheck, LogIn, Truck } from "lucide-react";
import logoMcom from "@/assets/logo192.png";
import storage from "@/utils/storage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [concessions, setConcessions] = useState([]);
  const [selectedConcession, setSelectedConcession] = useState("");
  const [sellerPassword, setSellerPassword] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [fleetPassword, setFleetPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingConcessions, setLoadingConcessions] = useState(true);
  const [loginType, setLoginType] = useState("concession");

  useEffect(() => {
    // Check if already logged in
    const session = storage.getJSON("emcom_session");
    if (session) {
      navigate("/");
    }
    fetchConcessions();
  }, [navigate]);

  const fetchConcessions = async () => {
    try {
      const response = await axios.get(`${API}/concessions`);
      setConcessions(response.data || []);
    } catch (error) {
      console.error("Error fetching concessions:", error);
      toast.error("Erreur de connexion au serveur");
    } finally {
      setLoadingConcessions(false);
    }
  };

  const handleConcessionLogin = async (e) => {
    e.preventDefault();
    if (!selectedConcession) {
      toast.error("Veuillez sélectionner une concession");
      return;
    }
    if (!sellerPassword) {
      toast.error("Veuillez entrer le mot de passe");
      return;
    }

    setLoading(true);
    try {
      // Connexion vendeur avec mot de passe
      const response = await axios.post(`${API}/concessions/seller-login`, {
        concession_id: selectedConcession,
        password: sellerPassword,
      });

      if (response.data.success) {
        storage.setJSON("emcom_session", {
          concession_id: response.data.concession_id,
          concession_name: response.data.concession_name,
          city: response.data.city,
          brands: response.data.brands,
          min_margin_rate: response.data.min_margin_rate || 0.04,
          is_admin: false,
          logged_in_at: new Date().toISOString(),
        });
        
        toast.success(`Bienvenue ! ${response.data.concession_name}`);
        navigate("/");
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.response?.status === 401) {
        toast.error("Mot de passe incorrect");
      } else {
        toast.error("Erreur de connexion");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    if (!adminPassword) {
      toast.error("Veuillez entrer le mot de passe administrateur");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/front-login`, {
        password: adminPassword,
      });

      if (response.data.success) {
        storage.setJSON("emcom_session", {
          concession_id: response.data.concession_id,
          concession_name: response.data.concession_name,
          city: response.data.city,
          brands: response.data.brands,
          is_admin: true,
          logged_in_at: new Date().toISOString(),
        });
        
        toast.success("Bienvenue Administrateur !");
        navigate("/");
      }
    } catch (error) {
      console.error("Admin login error:", error);
      toast.error("Mot de passe incorrect");
    } finally {
      setLoading(false);
    }
  };

  // Fleet Login
  const handleFleetLogin = async (e) => {
    e.preventDefault();
    if (!fleetPassword) {
      toast.error("Veuillez entrer le mot de passe Fleet");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/fleet/login`, {
        password: fleetPassword,
      });

      if (response.data.success) {
        storage.setJSON("emcom_session", {
          concession_id: "fleet",
          concession_name: "Fleet",
          city: "",
          brands: response.data.brands,
          is_fleet: true,
          is_admin: false,
          logged_in_at: new Date().toISOString(),
        });
        
        toast.success("Bienvenue Fleet !");
        navigate("/");
      }
    } catch (error) {
      console.error("Fleet login error:", error);
      if (error.response?.status === 401) {
        toast.error("Mot de passe incorrect");
      } else {
        toast.error("Erreur de connexion");
      }
    } finally {
      setLoading(false);
    }
  };

  const selectedConcessionData = concessions.find(c => c.id === selectedConcession);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#212E38] to-[#1a252e] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 shadow-lg overflow-hidden bg-white logo-container">
            <img src={logoMcom} alt="Groupe Verbaere" className="w-16 h-16 object-contain" />
          </div>
          <h1 className="text-3xl font-heading font-black text-white tracking-tight">e-MCom</h1>
          <p className="text-gray-400 text-xs mt-1">by Verbaere Performance Suite</p>
        </div>

        {/* Login Card */}
        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur">
          <CardContent className="pt-6">
            <Tabs value={loginType} onValueChange={setLoginType} className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6">
                <TabsTrigger value="concession" className="flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Showroom
                </TabsTrigger>
                <TabsTrigger value="fleet" className="flex items-center gap-2">
                  <Truck className="w-4 h-4" />
                  Fleet
                </TabsTrigger>
                <TabsTrigger value="admin" className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  Admin
                </TabsTrigger>
              </TabsList>

              {/* Concession Login Tab */}
              <TabsContent value="concession">
                <form onSubmit={handleConcessionLogin} className="space-y-5" autoComplete="on">
                  {/* Champ caché pour le username (aide le navigateur à sauvegarder) */}
                  <input type="hidden" name="username" value="showroom" autoComplete="username" />
                  
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      Concession
                    </label>
                    <Select 
                      value={selectedConcession} 
                      onValueChange={setSelectedConcession}
                      disabled={loadingConcessions}
                    >
                      <SelectTrigger data-testid="concession-select" className="h-12 text-gray-900 dark:text-gray-900">
                        <SelectValue placeholder={loadingConcessions ? "Chargement..." : "Sélectionner votre concession"} />
                      </SelectTrigger>
                      <SelectContent>
                        {concessions.map((c) => (
                          <SelectItem key={c.id} value={c.id} data-testid={`concession-option-${c.id}`}>
                            {c.name} - {c.city}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedConcessionData && (
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500 mb-2">Marques disponibles:</p>
                      <div className="flex flex-wrap gap-1">
                        {selectedConcessionData.brands.map(brand => (
                          <span 
                            key={brand} 
                            className="px-2 py-1 text-xs font-medium bg-[#0A0A0A] text-white rounded"
                          >
                            {brand}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label htmlFor="showroom-password" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Mot de passe
                    </label>
                    <Input
                      id="showroom-password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={sellerPassword}
                      onChange={(e) => setSellerPassword(e.target.value)}
                      placeholder="Entrez le mot de passe"
                      className="h-12"
                      data-testid="seller-password-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white font-medium"
                    disabled={loading || loadingConcessions || !selectedConcession || !sellerPassword}
                    data-testid="login-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Connexion...
                      </>
                    ) : (
                      <>
                        <LogIn className="w-4 h-4 mr-2" />
                        Connexion
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Admin Login Tab */}
              <TabsContent value="admin">
                <form onSubmit={handleAdminLogin} className="space-y-5" autoComplete="on">
                  {/* Champ caché pour le username (aide le navigateur à sauvegarder) */}
                  <input type="hidden" name="username" value="admin" autoComplete="username" />
                  
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 rounded-lg p-4">
                    <p className="text-sm text-blue-800 flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4" />
                      Accès administrateur - Toutes les marques
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="admin-password" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Mot de passe administrateur
                    </label>
                    <Input
                      id="admin-password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      placeholder="Entrez le mot de passe admin"
                      className="h-12"
                      data-testid="admin-password-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium"
                    disabled={loading}
                    data-testid="admin-login-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Connexion...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="w-4 h-4 mr-2" />
                        Accès Administrateur
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>

              {/* Fleet Login Tab */}
              <TabsContent value="fleet">
                <form onSubmit={handleFleetLogin} className="space-y-5" autoComplete="on">
                  {/* Champ caché pour le username (aide le navigateur à sauvegarder) */}
                  <input type="hidden" name="username" value="fleet" autoComplete="username" />
                  
                  <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-100 rounded-lg p-4">
                    <p className="text-sm text-emerald-800 flex items-center gap-2">
                      <Truck className="w-4 h-4" />
                      Accès Fleet - Toutes les marques
                    </p>
                    <p className="text-xs text-emerald-600 mt-1">
                      Payplan Fleet spécifique
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="fleet-password" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      Mot de passe Fleet
                    </label>
                    <Input
                      id="fleet-password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={fleetPassword}
                      onChange={(e) => setFleetPassword(e.target.value)}
                      placeholder="Entrez le mot de passe Fleet"
                      className="h-12"
                      data-testid="fleet-password-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-medium"
                    disabled={loading || !fleetPassword}
                    data-testid="fleet-login-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Connexion...
                      </>
                    ) : (
                      <>
                        <Truck className="w-4 h-4 mr-2" />
                        Accès Fleet
                      </>
                    )}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-gray-500 text-xs mt-6">
          © {new Date().getFullYear()} e-MCom by Verbaere Performance Suite
        </p>
      </div>
    </div>
  );
}
